import { skipToken } from "@reduxjs/toolkit/query";
import { useGetPatientDisputesQuery } from "src/api/services/invoicesApi";
import Spinner from "../Spinner";
import { Button, Typography } from "@mui/material";
import DataTable from "../DataTable";
import { Column, Dispute, PatientProfile } from "src/api/types";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

const useColumns = (): Column<Dispute>[] => {
  return [
    {
      id: "invoice_item",
      label: "Invoice item",
      orderable: false,
      minWidth: 200,
      align: "center",
      format: (row) => row.charge.invoice.description,
      render: (row) => {
        if (row.charge == null || row.charge.invoice == null) return "N/A";

        return <Typography>{row.charge.invoice.description}</Typography>;
      },
    },
    {
      id: "status",
      label: "Status",
      orderable: false,
      minWidth: 80,
      align: "center",
      format: (row) => row.status,
      render: (row) => <Typography>{row.status}</Typography>,
    },
    {
      id: "reason",
      label: "Reason",
      orderable: false,
      minWidth: 90,
      align: "center",
      format: (row) => row.reason,
      render: (row) => <Typography>{row.reason}</Typography>,
    },
    {
      id: "dispute_date",
      label: "Dispute date",
      orderable: false,
      minWidth: 90,
      align: "center",
      format: (row) => row.created.toString(),
      render: (row) => {
        const date = new Date(row.created * 1000);

        return <Typography>{format(date, "MM/dd/yyyy")}</Typography>;
      },
    },
    {
      id: "charge_date",
      label: "Charge date",
      orderable: false,
      minWidth: 90,
      align: "center",
      format: (row) => row.charge.created.toString(),
      render: (row) => {
        if (row.charge == null) return "N/A";

        const date = new Date(row.charge.created * 1000);

        return <Typography>{format(date, "MM/dd/yyyy")}</Typography>;
      },
    },

    {
      id: "dispute_amount",
      label: "Dispute amount",
      orderable: false,
      minWidth: 90,
      align: "center",
      format: (row) => row.amount.toString(),
      render: (row) => (
        <Typography>{`$${(row.amount / 100).toFixed(2)}`}</Typography>
      ),
    },
    {
      id: "view_dispute",
      label: "View dispute",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (row) => row.id,
      render: (row) => {
        const stripeUrl = import.meta.env.PROD
          ? `https://dashboard.stripe.com/disputes/${row.id}`
          : `https://dashboard.stripe.com/test/disputes/${row.id}`;

        return (
          <Link to={stripeUrl} target="_blank">
            <Button variant="secondary">
              <Typography fontWeight={700} variant="body1">
                {"View"}
              </Typography>
            </Button>
          </Link>
        );
      },
    },
  ];
};

interface Props {
  patient: PatientProfile;
}

const PatientDisputes = ({ patient }: Props) => {
  const { data = [], isLoading } = useGetPatientDisputesQuery(
    patient.stripe_customer_id ?? skipToken,
  );

  const columns = useColumns();

  const height = useMemo(() => window.innerHeight - 170 - 64 - 32, []);

  return isLoading ? (
    <Spinner />
  ) : data.length === 0 ? (
    <Typography>Patient has no disputes at this time.</Typography>
  ) : (
    <DataTable data={data} columns={columns} primaryKey="id" height={height} />
  );
};

export default PatientDisputes;
